<template>
  <div class="image-viewer">
    <!-- If we have a link, this becomes an <a> tag.
      The referrerpolicy here is to allow better url tracking -->
    <component
      :is="componentTag"
      class="comet-thumbnail asset-module__image"
      :class="{
        'asset-module__image--transparent': useTransparentBackground,
        'comet-thumbnail--contain': imageSettings === 'letterbox' && url,
      }"
      :href="link || undefined"
      :target="link ? '_blank' : undefined"
      :referrerpolicy="link ? 'no-referrer-when-downgrade' : undefined"
      @click="navigate"
    >
      <div
        ref="backgroundImage"
        class="comet-thumbnail__inner"
        :class="{ 'image-viewer__background--draggable': canMoveImage }"
        :style="backgroundImageStyle"
        v-if="imageUrl"
      >
        <img
          :src="imageUrl"
          :alt="altText"
          class="comet-thumbnail__print-only-image"
        >
        <span
          class="comet-screenreader-only"
          v-if="altText"
        >
          {{ altText }}
        </span>
      </div>
      <LoadingIndicator
        absolute
        v-else-if="thumbnailIsLoading"
        class="asset-module__image-loading-indicator"
      />
      <div
        class="comet-thumbnail__inner"
        v-else
      >
        <!-- For images without thumbnails, show the image icon instead -->
        <NebulaIcon
          symbol-id="asset-image"
          class-override="comet-thumbnail__icon"
        />
      </div>
    </component>
    <template v-if="allowZoom">
      <K5ZoomButton
        v-if="useK5ZoomButton"
        class="image-viewer__zoom-button"
        :class="`image-viewer__zoom-button--${currentZoomButtonPosition}`"
        :aria-label="$t('Zoom Image')"
        @click="zoomImage"
      />
      <ZoomButton
        v-else
        class="image-viewer__zoom-button"
        :class="`image-viewer__zoom-button--${currentZoomButtonPosition}`"
        :aria-label="$t('Zoom Image')"
        @click="zoomImage"
      />
    </template>
  </div>
</template>

<script>
import {
  cloneDeep, get, isNumber,
} from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { NebulaButton, NebulaIcon } from '@discoveryedu/nebula-components';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import K5ZoomButton from '@/components/ui-elements/K5ZoomButton.vue';
import ZoomButton from '@/components/ui-elements/ZoomButton.vue';
import navigation from '@/mixins/navigation';
import thumbnailLoader from '@/mixins/thumbnailLoader';
import imageMixin from '@/mixins/image';
import zoomButtonPosition from '@/mixins/zoomButton';
import { bus } from '@/lib/eventBus';
import { formatUrl } from '@/lib/utils/url';
import * as types from '@/lib/constants/store';
import {
  useEditorStore,
  useModalStore,
} from '@/stores';

export default {
  name: 'ImageViewer',
  components: {
    NebulaButton,
    NebulaIcon,
    LoadingIndicator,
    ZoomButton,
    K5ZoomButton,
  },
  mixins: [navigation, thumbnailLoader, imageMixin, zoomButtonPosition],
  props: {
    allowZoom: {
      type: Boolean,
      default: false,
    },
    imageSettings: {
      type: String,
      default: null,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    active: {
      type: Boolean,
      default: false,
    },
    scale: {
      type: Number,
      default: 1,
    },
    canvasName: {
      type: String,
      default: null,
    },
    useTransparentBackground: Boolean,
    themeName: {
      type: String,
      default: 'default',
    },
  },
  data() {
    return {
      imageDragInfo: null,
      useLargeImageUrls: true, // used by the thumbnail loader
    };
  },
  mounted() {
    this.onCanMoveImageChanged();
  },
  beforeUnmount() {
    this.disableMoveImage();
  },
  computed: {
    ...mapState(useEditorStore, [
      'domains',
      'getModuleById',
    ]),
    altText() {
      return this.cachedAsset ? this.cachedAsset.image_data?.alt_text : null;
    },
    useK5ZoomButton() {
      return [
        'k-2-science-earth',
        'k-2-science-life',
        'k-2-science-physical',
      ].includes(this.themeName);
    },
    componentTag() {
      if (this.link) return 'a';

      if (
        ['page', 'window'].includes(this.moduleData.options?.action)
        && this.moduleData.options.target_page
        && this.mode === 'VIEW'
      ) {
        return 'button';
      }

      return 'div';
    },
    link() {
      // Do not navigate in edit mode
      if (this.mode === 'EDIT') return null;

      if (this.moduleData.options?.action !== 'link') return null;

      const target = formatUrl(get(this, 'moduleData.options.target', ''));

      // Use the app domain to figure out what domain we should be using
      const domain = this.domains.appDomain
        .replace(/(.*?)(discoveryeducation\.(com|ca|co\.uk))(.*)/, '$2');

      // Replace any asset links with the app domain
      const newTarget = target.replace(
        /(discoveryeducation\.(com|ca|co\.uk))(\/learn\/(player|videos|channels|glossary|techbook|assessment)|\/static\/interactives)/gi,
        `${domain}$3`,
      );

      return newTarget;
    },
    backgroundImageStyle() {
      const style = { backgroundImage: `url('${this.validThumbnailUrl}')` };
      if (!this.imageDragInfo && !this.imageOffset) return style;
      const offset = this.imageDragInfo || {
        offsetX: this.imageOffset.x_position,
        offsetY: this.imageOffset.y_position,
      };
      return {
        ...style,
        backgroundPosition: `${offset.offsetX}px ${offset.offsetY}px`,
      };
    },
    currentZoomButtonPosition() {
      return this.zoomButtonPosition();
    },
  },
  watch: {
    canMoveImage() {
      this.onCanMoveImageChanged();
    },
  },
  methods: {
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
    ]),
    ...mapActions(useEditorStore, [
      types.VIEW_MODAL_CANVAS_BY_ID,
      types.VIEW_PAGE_BY_ID,
      types.VIEW_NEXT_PAGE,
      types.VIEW_PREV_PAGE,
      types.UPDATE_MODULES,
    ]),
    onCanMoveImageChanged() {
      if (this.canMoveImage) {
        this.enableMoveImage();
      } else {
        this.disableMoveImage();
      }
    },
    enableMoveImage() {
      this.disableMoveImage();
      bus.on('document:mousedown', this.onMousedown);
      document.addEventListener('mousemove', this.onMousemove);
      document.addEventListener('mouseup', this.onMouseup);
      bus.on('module:resize', this.onModuleResize);
      if (this.imageOffset) {
        this.updateImageOffsetPercents(this.imageOffset.x_position, this.imageOffset.y_position);
      }
    },
    disableMoveImage() {
      bus.off('document:mousedown', this.onMousedown);
      document.removeEventListener('mousemove', this.onMousemove);
      document.removeEventListener('mouseup', this.onMouseup);
      bus.off('module:resize', this.onModuleResize);
    },
    /*
      Saves how far offset the background image is from its default center position
      as a percentage of the background image size. These percentages will be used to
      determine its new position after a module resize.
    */
    updateImageOffsetPercents(offsetX, offsetY) {
      const bgImageDefaults = this.getBackgroundImageDefaults();
      this.offsetPercents = {
        offsetXPercent: (offsetX - bgImageDefaults.offsetX) / bgImageDefaults.width,
        offsetYPercent: (offsetY - bgImageDefaults.offsetY) / bgImageDefaults.height,
      };
    },
    /*
      On resize, attempts to keep the background image offset the same relative to
      the size of the background image when the module and background image
      are resized.
    */
    onModuleResize(moduleId) {
      if (moduleId === this.moduleData.id) {
        this.$nextTick(() => {
          if (this.imageOffset && this.offsetPercents) {
            const bgImageDefaults = this.getBackgroundImageDefaults();
            const dragLimits = this.getImageDragLimits(bgImageDefaults);
            const offsets = {
              offsetX: Math.round(this.offsetPercents.offsetXPercent
                * bgImageDefaults.width + bgImageDefaults.offsetX),
              offsetY: Math.round(this.offsetPercents.offsetYPercent
              * bgImageDefaults.height + bgImageDefaults.offsetY),
            };
            this.applyDragLimits(dragLimits, offsets);
            if (!this.imageDragInfo) {
              this.imageDragInfo = {
                disableMousemove: true,
                startOffsetY: this.imageOffset.y_position,
                startOffsetX: this.imageOffset.x_position,
              };
            }
            this.imageDragInfo.offsetX = offsets.offsetX;
            this.imageDragInfo.offsetY = offsets.offsetY;
          }
        });
      }
    },
    /**
     * Gets the default offset and size of the background image based on
     * the current image settings and current size of the image viewer.
     * i.e. The position and size of the background image before the user
     * has moved it to a new position.
     */
    getBackgroundImageDefaults() {
      const imageViewerRef = this.$el;
      let bgImageDefaults = {
        offsetY: 0,
        offsetX: 0,
        width: 0,
        height: 0,
      };
      if (imageViewerRef) {
        const bgContainerAspectRatio = imageViewerRef.clientHeight
          / imageViewerRef.clientWidth;
        const { imageAspectRatio, imageWidth, imageHeight } = this.imageInfo;
        let bgImageScale;
        if (this.imageSettings === 'fill') {
          bgImageScale = imageAspectRatio > bgContainerAspectRatio
            ? imageViewerRef.clientWidth / imageWidth
            : imageViewerRef.clientHeight / imageHeight;
        } else if (this.imageSettings === 'letterbox') {
          bgImageScale = imageAspectRatio > bgContainerAspectRatio
            ? imageViewerRef.clientHeight / imageHeight
            : imageViewerRef.clientWidth / imageWidth;
        }

        if (bgImageScale) {
          const bgImageWidth = bgImageScale * imageWidth;
          const bgImageHeight = bgImageScale * imageHeight;
          bgImageDefaults = {
            offsetY: (imageViewerRef.clientHeight / 2) - (bgImageHeight / 2),
            offsetX: (imageViewerRef.clientWidth / 2) - (bgImageWidth / 2),
            width: bgImageWidth,
            height: bgImageHeight,
          };
        }
      }
      return bgImageDefaults;
    },
    /**
     * Gets the drag limits for the background image based on the current
     * selected shape mask. These are used to keep the background image from
     * being dragged outside the shape mask area.
     */
    getImageDragLimits(bgImageDefaults) {
      const dragLimits = {
        offsetX: null,
        offsetY: null,
      };
      const shape = this.moduleData?.options?.shape;
      if (shape) {
        const gridItem = this.$el.closest(`[id='${this.moduleData.id}']`);
        if (gridItem) {
          const backgroundWidthPadded = bgImageDefaults.width + 2;
          const backgroundHeightPadded = bgImageDefaults.height + 2;
          if (shape === 'circle') {
            const radius = Math.min(gridItem.clientWidth, gridItem.clientHeight) / 2;
            const diameter = radius * 2;
            const circleXPadding = gridItem.clientWidth / 2 - radius;
            const circleYPadding = gridItem.clientHeight / 2 - radius;
            dragLimits.offsetX = diameter <= backgroundWidthPadded
              ? {
                min: gridItem.clientWidth - circleXPadding - bgImageDefaults.width,
                max: circleXPadding,
              } : {
                min: circleXPadding,
                max: circleXPadding + diameter - bgImageDefaults.width,
              };
            dragLimits.offsetY = diameter <= backgroundHeightPadded
              ? {
                min: gridItem.clientHeight - circleYPadding - bgImageDefaults.height,
                max: circleYPadding,
              } : {
                min: circleYPadding,
                max: circleYPadding + diameter - bgImageDefaults.height,
              };
          } else if (shape === 'pentagon') {
            const svgHeight = 878;
            const svgWidth = 827;

            let pentagonXPadding = 0;
            let pentagonYPadding = 0;
            let scaledSvgHeight = 0;
            let scaledSvgWidth = 0;

            if (gridItem.clientHeight / gridItem.clientWidth > svgHeight / svgWidth) {
              // We'll have padding on the top and bottom. Our calculations should
              // only depend on the module width
              scaledSvgHeight = gridItem.clientWidth * (svgHeight / svgWidth);
              scaledSvgWidth = gridItem.clientWidth;
              pentagonYPadding = (gridItem.clientHeight - scaledSvgHeight) / 2;
            } else {
              // We'll have padding on the sides. Our calculations should
              // only depend on the module height
              scaledSvgHeight = gridItem.clientHeight;
              scaledSvgWidth = gridItem.clientHeight * (svgWidth / svgHeight);
              pentagonXPadding = (gridItem.clientWidth - scaledSvgWidth) / 2;
            }

            dragLimits.offsetX = scaledSvgWidth <= backgroundWidthPadded
              ? {
                min: gridItem.clientWidth - pentagonXPadding - bgImageDefaults.width,
                max: pentagonXPadding,
              } : {
                min: pentagonXPadding,
                max: pentagonXPadding + scaledSvgWidth - bgImageDefaults.width,
              };
            dragLimits.offsetY = scaledSvgHeight <= backgroundHeightPadded
              ? {
                min: gridItem.clientHeight - pentagonYPadding - bgImageDefaults.height,
                max: pentagonYPadding,
              } : {
                min: pentagonYPadding,
                max: pentagonYPadding + scaledSvgHeight - bgImageDefaults.height,
              };
          } else if (['rounded_rectangle', 'speech_bubble_lower_right', 'speech_bubble_lower_left', 'title_bubble'].includes(shape)) {
            const maskWidth = gridItem.clientWidth;
            const maskHeight = gridItem.clientHeight;
            dragLimits.offsetX = maskWidth <= backgroundWidthPadded
              ? {
                min: maskWidth - bgImageDefaults.width,
                max: 0,
              } : {
                min: 0,
                max: maskWidth - bgImageDefaults.width,
              };
            dragLimits.offsetY = maskHeight <= backgroundHeightPadded
              ? {
                min: maskHeight - bgImageDefaults.height,
                max: 0,
              } : {
                min: 0,
                max: maskHeight - bgImageDefaults.height,
              };
          }
        }
      }
      return dragLimits;
    },
    /**
     * Adjusts the provided image offsets to be within the specified drag limits.
     */
    applyDragLimits(dragLimits, imageOffsets) {
      const offsets = imageOffsets;
      if (dragLimits?.offsetX) {
        if (offsets.offsetX < dragLimits?.offsetX?.min) {
          offsets.offsetX = dragLimits?.offsetX?.min;
        } else if (offsets.offsetX > dragLimits?.offsetX?.max) {
          offsets.offsetX = dragLimits?.offsetX?.max;
        }
      }
      if (dragLimits?.offsetY) {
        if (offsets.offsetY < dragLimits?.offsetY?.min) {
          offsets.offsetY = dragLimits?.offsetY?.min;
        } else if (offsets.offsetY > dragLimits?.offsetY?.max) {
          offsets.offsetY = dragLimits?.offsetY?.max;
        }
      }
    },
    /**
     * Starts the background image drag operation if the background image is loaded.
     */
    onMousedown(event) {
      const backgroundImageRef = this.$refs?.backgroundImage;
      const startImageDrag = backgroundImageRef && event.target === backgroundImageRef;
      if (!startImageDrag) {
        this.imageDragInfo = null;
        return;
      }

      // This is required or dragging can freeze up in UI.
      event.preventDefault();

      const bgImageDefaults = this.getBackgroundImageDefaults();
      const dragLimits = this.getImageDragLimits(bgImageDefaults);
      const offsetY = isNumber(get(this, 'imageOffset.y_position'))
        ? this.imageOffset.y_position : bgImageDefaults.offsetY;
      const offsetX = isNumber(get(this, 'imageOffset.x_position'))
        ? this.imageOffset.x_position : bgImageDefaults.offsetX;
      const rotationDegrees = isNumber(get(this, 'moduleData.layout.rotate_degree'))
        ? this.moduleData.layout.rotate_degree : this.moduleData?.layout?.r;

      /*
        offsetX and offsetY are the offsets of the background image from
        the top left corner of the container that holds the background image.
        By default the background image is centered, but based on its size, it
        likely will have default offsets that are not equal to 0.
      */
      this.imageDragInfo = {
        dragStartX: event.clientX,
        dragStartY: event.clientY,
        offsetY,
        offsetX,
        startOffsetY: offsetY,
        startOffsetX: offsetX,
        dragLimits,
        rotationDegrees,
      };
    },
    /**
     * Handles moving the background image when dragging. Also handles adjusting the drag
     * location for module rotation.
     */
    onMousemove(event) {
      if (event.buttons !== 1 && this.imageDragInfo) {
        this.imageDragInfo = null;
        return;
      }
      if (this.imageDragInfo && !this.imageDragInfo.disableMousemove) {
        // This is required or dragging can freeze up in UI.
        event.preventDefault();

        let dragXOffset = (event.clientX - this.imageDragInfo.dragStartX) / this.scale;
        let dragYOffset = (event.clientY - this.imageDragInfo.dragStartY) / this.scale;

        const { rotationDegrees } = this.imageDragInfo;
        if (rotationDegrees && rotationDegrees !== 0) {
          /*
            Adjust the drag offset to account for module rotation. When the module is rotated
            and the user is dragging the image, this allows the image to move in the same direction
            that the mouse is moving regardless of the rotation degree.
          */
          // Straight line distance (radius) from the drag start point to current drag position.
          const offsetRadius = Math.sqrt((dragXOffset ** 2) + (dragYOffset ** 2));
          // Angle of the line from drag start point to current drag position.
          const offsetAngleRadians = Math.atan2(dragYOffset, dragXOffset);
          // Convert the current module rotation degrees to radians.
          const rotationDegreesInRadians = rotationDegrees * (Math.PI / 180);
          /*
            The total angle used to adjust the current drag position for module rotation.
            This is the angle of the straight line from drag start to current drag position
            minus the angle of the total rotation applied to the module.
          */
          const totalAngleRadians = offsetAngleRadians - rotationDegreesInRadians;
          /*
            Get the length of the opposite and adjacent sides of the right triangle created
            by our total angle calculated, which gives us the x and y offsets adjusted to the
            current module rotation.
          */
          dragXOffset = Math.cos(totalAngleRadians) * offsetRadius;
          dragYOffset = Math.sin(totalAngleRadians) * offsetRadius;
        }

        const moveToOffsets = {
          offsetY: Math.round(this.imageDragInfo.startOffsetY + dragYOffset),
          offsetX: Math.round(this.imageDragInfo.startOffsetX + dragXOffset),
        };

        this.applyDragLimits(this.imageDragInfo.dragLimits, moveToOffsets);

        /*
          Only update the imageDragInfo object if the image offsets have changed.
        */
        if (moveToOffsets.offsetY !== this.imageDragInfo.offsetY) {
          this.imageDragInfo.offsetY = moveToOffsets.offsetY;
        }
        if (moveToOffsets.offsetX !== this.imageDragInfo.offsetX) {
          this.imageDragInfo.offsetX = moveToOffsets.offsetX;
        }
      }
    },
    /**
     * Handles committing the drag operation.
     */
    onMouseup() {
      if (this.imageDragInfo) {
        const bgImageDefaults = this.getBackgroundImageDefaults();
        const dragLimits = this.getImageDragLimits(bgImageDefaults);
        this.applyDragLimits(dragLimits, this.imageDragInfo);
        const {
          offsetY, offsetX, startOffsetY, startOffsetX,
        } = this.imageDragInfo;
        if (offsetY !== startOffsetY || offsetX !== startOffsetX) {
          this.updateImageOffsetPercents(offsetX, offsetY);
          this.updateModuleImageOffsets(offsetX, offsetY);
        }
        this.imageDragInfo = null;
      }
    },
    /**
     * Handles committing the new offsets for the background image to the app store.
     */
    updateModuleImageOffsets(offsetX, offsetY) {
      const modToUpdate = cloneDeep(this.getModuleById(this.moduleData.id));
      if (modToUpdate) {
        modToUpdate.options.image_offset = { x_position: offsetX, y_position: offsetY };
        this[types.UPDATE_MODULES]({
          canvasName: this.canvasName,
          modules: [modToUpdate],
        });
      }
    },
    navigate(e) {
      this.handleViewModeAnchorClick(e);
      if (this.mode === 'EDIT') {
        // In edit mode, we should never navigate on click
        e.preventDefault();
      } else if (this.moduleData.options.action === 'page') {
        // Otherwise navigate if it's a button (anchor links handle themselves)
        const targetPage = this.moduleData.options.target_page;

        if (targetPage === 'PREV') {
          this[types.VIEW_PREV_PAGE]();
        } else if (targetPage === 'NEXT') {
          this[types.VIEW_NEXT_PAGE]();
        } else {
          this[types.VIEW_PAGE_BY_ID](targetPage);
        }
      } else if (this.moduleData.options.action === 'window') {
        this[types.VIEW_MODAL_CANVAS_BY_ID](this.moduleData.options.target_page);
      }
    },
    zoomImage() {
      this[types.OPEN_MODAL]({
        type: 'ImageZoomModal',
        url: this.url,
      });
    },
  },
};
</script>

<style lang="stylus">
.asset-module {
  &__image {
    border: none;
  }

  &__image-loading-indicator {
    z-index: 100;
  }
}

.image-viewer {
  height: 100%;
  position: relative;

  &__zoom-button {
    bottom: $nebula-space-1x;
    inset-inline-end: $nebula-space-1x;
    position: absolute;

    &--top-left {
      inset-block-end: auto;
      inset-block-start: $nebula-space-1x;
      inset-inline-end: auto;
      inset-inline-start: $nebula-space-1x;
    }
    &--top-right {
      inset-block-end: auto;
      inset-block-start: $nebula-space-1x;
      inset-inline-end: $nebula-space-1x;
      inset-inline-start: auto;
    }
    &--bottom-left {
      inset-block-end: $nebula-space-1x;
      inset-block-start: auto;
      inset-inline-end: auto;
      inset-inline-start: $nebula-space-1x;
    }
    &--bottom-right {
      inset-block-end: $nebula-space-1x;
      inset-block-start: auto;
      inset-inline-end: $nebula-space-1x;
      inset-inline-start: auto;
    }
  }

  &__background--draggable {
    cursor: move;
  }
}
</style>
